import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Divider
} from '@mui/material';
import {
  Close,
  GroupsOutlined,
  RecordVoiceOverOutlined,
  TabOutlined,
  RefreshOutlined,
  CancelOutlined,
  NoAccountsOutlined,
  VisibilityOutlined,
  VpnLockOutlined,
  FaceOutlined,
  FingerprintOutlined,
  SecurityOutlined,
  AssignmentOutlined,
  VideocamOutlined
} from '@mui/icons-material';

const InterviewGuidelinesModal = ({ open, onClose, onAccept }) => {
  const [checkedItems, setCheckedItems] = useState(new Array(12).fill(false));
  const [finalAgreed, setFinalAgreed] = useState(false);

  const guidelines = [
    {
      icon: <GroupsOutlined sx={{ color: "#1e3a8a" }} />,
      title: "1. Multiple Face Detection",
      desc: "Detects the presence of more than one person in front of the camera during the interview."
    },
    {
      icon: <RecordVoiceOverOutlined sx={{ color: "#1e3a8a" }} />,
      title: "2. Multiple Voice Detection",
      desc: "Detects additional voices or conversations other than the candidate's voice during the interview."
    },
    {
      icon: <TabOutlined sx={{ color: "#1e3a8a" }} />,
      title: "3. Tab Switching Detection",
      desc: "Detects when the candidate switches to another browser tab or application window."
    },
    {
      icon: <RefreshOutlined sx={{ color: "#1e3a8a" }} />,
      title: "4. Refreshing Page Detection",
      desc: "Detects when the interview page is refreshed during an active interview session."
    },
    {
      icon: <CancelOutlined sx={{ color: "#1e3a8a" }} />,
      title: "5. Closing Tab Detection",
      desc: "Detects when the candidate attempts to close the interview tab or browser window."
    },
    {
      icon: <NoAccountsOutlined sx={{ color: "#1e3a8a" }} />,
      title: "6. Face Missing Detection",
      desc: "Detects when the candidate's face is not visible to the camera for a certain period."
    },
    {
      icon: <VisibilityOutlined sx={{ color: "#1e3a8a" }} />,
      title: "7. Eye Gaze Detection",
      desc: "Detects prolonged eye movements away from the screen, indicating possible distractions or external assistance."
    },
    {
      icon: <VpnLockOutlined sx={{ color: "#1e3a8a" }} />,
      title: "8. VPN or Network Detection Error",
      desc: "Detects changes in VPN usage, IP address, or network connection during the interview."
    },
    {
      icon: <FaceOutlined sx={{ color: "#1e3a8a" }} />,
      title: "9. Face or Head Turning Detection",
      desc: "Detects excessive head movements or frequent turning away from the screen."
    },
    {
      icon: <FingerprintOutlined sx={{ color: "#1e3a8a" }} />,
      title: "10. Browser Fingerprint Detection",
      desc: "Detects changes in browser environment, device characteristics, or suspicious session behavior."
    },
    {
      icon: <SecurityOutlined sx={{ color: "#1e3a8a" }} />,
      title: "11. IP Logging Detection",
      desc: "Logs and monitors the candidate's IP address to maintain interview security and audit records."
    },
    {
      icon: <VideocamOutlined sx={{ color: "#1e3a8a" }} />,
      title: "12. Session Recording",
      desc: "Your webcam video and microphone audio for this interview session will be recorded and securely stored for review by the hiring team."
    }
  ];

  const handleCheck = (index) => {
    const newChecked = [...checkedItems];
    newChecked[index] = !newChecked[index];
    setCheckedItems(newChecked);
  };

  const allChecked = checkedItems.every(Boolean) && finalAgreed;

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCheckedItems(new Array(12).fill(false));
      setFinalAgreed(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#1e3a8a'
          }}>
            <AssignmentOutlined />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">Interview Guidelines</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Please read all guidelines carefully. You must agree to these guidelines to start your interview.
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <Divider />

      <DialogContent sx={{ p: 0 }}>
        <Stack divider={<Divider />}>
          {guidelines.map((item, index) => (
            <Box
              key={index}
              onClick={() => handleCheck(index)}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: '#f8fafc' }
              }}
            >
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.5,
                border: '1px solid #e2e8f0'
              }}>
                {item.icon}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1e293b' }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, lineHeight: 1.5 }}>
                  {item.desc}
                </Typography>
              </Box>
              <Checkbox
                checked={checkedItems[index]}
                onChange={(e) => {
                  e.stopPropagation();
                  handleCheck(index);
                }}
                sx={{ mt: -0.5 }}
              />
            </Box>
          ))}
        </Stack>

        <Box sx={{ p: 2, bgcolor: '#f1f5f9' }}>
          <Box
            onClick={() => setFinalAgreed(!finalAgreed)}
            sx={{
              p: 2,
              bgcolor: '#eef2ff',
              borderRadius: 1,
              border: '1px solid #bfdbfe',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#eff6ff' }
            }}
          >
            <Checkbox
              checked={finalAgreed}
              onChange={(e) => {
                e.stopPropagation();
                setFinalAgreed(!finalAgreed);
              }}
              sx={{ mt: -0.5 }}
            />
            <Typography variant="body2" fontWeight="bold" sx={{ color: '#1e3a8a', lineHeight: 1.5 }}>
              I have read, understood, and agree to the interview monitoring and proctoring policies. I understand that suspicious activities may be recorded and may impact my interview evaluation.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#64748b',
            borderColor: '#e2e8f0',
            px: 4,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 'bold',
            borderRadius: 1.5,
            '&:hover': {
              borderColor: '#cbd5e1',
              bgcolor: '#f8fafc'
            }
          }}
        >
          Close
        </Button>
        <Button
          onClick={onAccept}
          variant="contained"
          disabled={!allChecked}
          sx={{
            bgcolor: '#1e3a8a !important',
            color: 'white',
            px: 8,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 'bold',
            borderRadius: 1.5,
            fontSize: '1rem',
            '&:disabled': {
              bgcolor: '#cbd5e1 !important',
              color: '#94a3b8'
            }
          }}
        >
          I Agree & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterviewGuidelinesModal;
